import type { PersonName } from "@/lib/person-name"

export type MentionCategory =
  | "hero"
  | "loc"
  | "cam"
  | "prop"
  | "shot"
  | "reference"
  | "attr"
  | "file"
  | "person"
  | "task"
  | "script"
  | "node"
  | "img"
  | "vid"

export type MentionItem = {
  category: MentionCategory
  /** Сущность: id героя/локации/камеры/реквизита, порядковый номер референса или стабильный id атрибута. */
  id: number
  /** Состояние реквизита (@prop[id:stateId]); в задачах — ещё образ героя и вариант локации. */
  stateId?: number | null
  /** Ключ атрибута профиля (role, description, …); только для category === "attr". */
  attrKey?: string
  /** Отображаемая подпись в чипе (имя героя, название локации/камеры, @reference_N). */
  label: string
  subtitle?: string | null
  imageUrl?: string | null
  /** Reference media kind — drives AudioLines/Film thumb when imageUrl is null. */
  mediaType?: "image" | "video" | "audio" | "file"
  /** Silver padded chrome for prop front-view thumbs (not reference sheets). */
  frontViewChrome?: boolean
  /** Привязан к текущему шоту (герой/локация/реквизит) — поднимается в верхний блок подсказок. */
  pinned?: boolean
  /**
   * false — пункт нужен, чтобы старый токен (@reference_N) собрался в чип,
   * но в меню @ и ghost-подсказке его нет.
   */
  autocomplete?: boolean
  person?: PersonName
}

export const CATEGORY_ORDER: MentionCategory[] = ["hero", "loc", "prop", "node", "reference", "attr", "person", "file"]

/** Камеры есть в mention items (labels/posters), но не в @ / ghost autocomplete. */
export function isMentionAutocompleteCategory(category: string): boolean {
  return category !== "cam"
}

/** Пункт виден в меню @ и в ghost-подсказке. */
export function mentionInMenu(item: { category: string; autocomplete?: boolean }): boolean {
  return item.autocomplete !== false && isMentionAutocompleteCategory(item.category)
}

/** Ключ для lookup mention-айтема (картинка, label) по attrs. */
export function mentionKey(
  category: MentionCategory,
  id: number,
  stateId?: number | null,
  attrKey?: string | null,
): string {
  if (category === "attr") return `attr:${attrKey ?? id}`
  if (category === "prop") return `prop:${id}:${stateId ?? ""}`
  if ((category === "hero" || category === "loc") && stateId != null) return `${category}:${id}:${stateId}`
  return `${category}:${id}`
}

/** Подпись чипа: attr и задача без @, остальное с @. */
export function mentionDisplayLabel(category: MentionCategory, label: string): string {
  if (category === "attr" || category === "task") return label
  return label.startsWith("@") ? label : `@${label}`
}

/** Attrs mention-ноды из autocomplete/ghost item. */
export function mentionAttrsFromItem(item: MentionItem) {
  return {
    category: item.category,
    id: item.id,
    label: item.label,
    ...(item.stateId != null ? { stateId: item.stateId } : {}),
    ...(item.attrKey != null ? { attrKey: item.attrKey } : {}),
  }
}

/** Стабильный текстовый токен для бэкенда. */
export function mentionToken(
  category: MentionCategory,
  id: number,
  label: string,
  stateId?: number | null,
  attrKey?: string | null,
): string {
  if (category === "attr") return attrKey ? `@attr[${attrKey}]` : label
  if (category === "reference") return label.startsWith("@") ? label : `@reference_${id}`
  if (category === "img") return `@img_${id}`
  if (category === "vid") return `@vid_${id}`
  if (category === "prop" && stateId != null) return `@prop[${id}:${stateId}]`
  return `@${category}[${id}]`
}
